import type {
  ActionResult,
  Booking,
  BookingStatus,
  CompanyPricing,
  CompanyState,
  Driver,
  DriverDocsStatus,
  Listing,
  Plan,
  PlanId,
  PublishStatus,
  Unit,
  UnitStatus,
} from "../types/company";
import {
  BLOCKING_STATUSES,
  PLANS,
  TODAY,
  URGENT_OPTIONS,
} from "../data/company";
import { addDaysIso, daysBetween } from "./format";
import { getDict } from "../i18n";

export { addDaysIso, daysBetween };

/** "2026-10-05T14:03" — the demo clock's current time. */
function nowStamp(): string {
  return `${TODAY}T${new Date().toTimeString().slice(0, 5)}`;
}

/* ---------- Names ---------- */

/** "Tesla Model 3"; "…" while the listing isn't loaded (bookings can arrive first) or no longer exists. */
export const listingName = (l: Pick<Listing, "brand" | "model"> | undefined) =>
  l ? `${l.brand} ${l.model}` : "…";

/** "15 Avenue de la République, 75011 Paris, France" — only the city is public. */
export const pickupAddress = (l: Pick<Listing, "street" | "postalCode" | "city" | "country">) =>
  [l.street, [l.postalCode, l.city].filter(Boolean).join(" "), l.country]
    .map((part) => part.trim())
    .filter(Boolean)
    .join(", ");

export const driverName = (d: Pick<Driver, "firstName" | "lastName"> | undefined) =>
  d ? `${d.firstName} ${d.lastName}` : "…";

export const initials = (d: Pick<Driver, "firstName" | "lastName"> | undefined) =>
  d ? (d.firstName[0] ?? "") + (d.lastName[0] ?? "") : "";

/** Translated plan name ("Free" / "Gratuit"…). */
export const planName = (p: Pick<Plan, "id">) => getDict().company.plans[p.id].name;

/* ---------- Lookups ---------- */

export const findListing = (s: CompanyState, id: string): Listing | undefined =>
  s.listings.find((l) => l.id === id);
export const findUnit = (s: CompanyState, id: string) =>
  s.units.find((u) => u.id === id);
export const findDriver = (s: CompanyState, id: string): Driver | undefined =>
  s.drivers.find((d) => d.id === id);
export const findBooking = (s: CompanyState, id: string) =>
  s.bookings.find((b) => b.id === id);

export const unitsOf = (s: CompanyState, listingId: string) =>
  s.units.filter((u) => u.listingId === listingId && u.status !== "retired");

export function currentPlan(s: CompanyState): Plan | null {
  return s.subscription
    ? (PLANS.find((p) => p.id === s.subscription!.planId) ?? null)
    : null;
}

/** Published listings count against the plan limit. */
export const activeListingCount = (s: CompanyState) =>
  s.listings.filter((l) => l.publish === "published").length;

/** Free launch (GET /company/subscription): every company publishes for free. */
export const isLaunch = (s: CompanyState) => s.pricing?.mode === "launch";

/** Max active listings, null = unlimited. Before the API answers, the demo plan's limit. */
export function listingLimit(s: CompanyState): number | null {
  if (s.pricing) return s.pricing.listingLimit;
  return currentPlan(s)?.listingLimit ?? 0;
}

/** "5 active listings · Free launch access", "3 / 10 active listings · Plan Pro" or "No active plan". */
export function usageLabel(s: CompanyState): string {
  const t = getDict().company;
  const n = activeListingCount(s);
  if (isLaunch(s)) return t.launch.usage(n, s.pricing!.listingLimit);
  const plan = currentPlan(s);
  return plan && !s.pricing ? t.sidebar.usage(n, plan.listingLimit, planName(plan)) : t.sidebar.noPlan;
}

/** Sponsored right now (real date: sponsorships come from the API). */
export const isUrgent = (l: Listing) =>
  !!l.urgentUntil && l.urgentUntil >= new Date().toISOString().slice(0, 10);

/** Visible to drivers: published, by a verified company with a live plan. */
export function publicStatus(s: CompanyState, l: Listing): { visible: boolean; text: string } {
  const t = getDict().company.visibility;
  const reason =
    l.publish !== "published"
      ? t.reasons[l.publish]
      : s.profile.verification !== "verified"
        ? t.reasons.notVerified
        : // Once the API pricing is loaded, the backend decides (no subscriptions yet)
          !s.pricing && (!s.subscription || ["cancelled", "expired"].includes(s.subscription.status))
          ? t.reasons.noPlan
          : "";
  return reason ? { visible: false, text: t.hidden(reason) } : { visible: true, text: t.visible };
}

/* ---------- Availability ---------- */

export interface Conflict {
  type: "booking" | "block";
  ref: string;
  start: string;
  end: string;
}

/** Bookings and blocks overlapping [start, end) on a unit. */
export function unitConflicts(
  s: CompanyState,
  unitId: string,
  start: string,
  end: string,
  ignoreBookingId?: string,
): Conflict[] {
  const unit = findUnit(s, unitId);
  const bookings = s.bookings
    .filter(
      (b) =>
        b.unitId === unitId &&
        b.id !== ignoreBookingId &&
        BLOCKING_STATUSES.includes(b.status) &&
        b.start < end &&
        start < b.end,
    )
    .map((b) => ({ type: "booking" as const, ref: b.id, start: b.start, end: b.end }));
  const blocks = (unit?.blocks ?? [])
    .filter((x) => x.start < end && start < x.end)
    .map((x) => ({ type: "block" as const, ref: x.reason || getDict().company.errors.blocked, start: x.start, end: x.end }));
  return [...bookings, ...blocks];
}

export function unitFree(
  s: CompanyState,
  unit: Unit,
  start: string,
  end: string,
  ignoreBookingId?: string,
): boolean {
  return (
    unit.status === "active" &&
    unitConflicts(s, unit.id, start, end, ignoreBookingId).length === 0
  );
}

export function availableCount(
  s: CompanyState,
  listingId: string,
  start: string,
  end: string,
): number {
  return unitsOf(s, listingId).filter((u) => unitFree(s, u, start, end)).length;
}

/** Units free today, across the whole fleet. */
export const availableToday = (s: CompanyState) =>
  s.units.filter((u) => unitFree(s, u, TODAY, addDaysIso(TODAY, 1))).length;

/* ---------- Driver documents ---------- */

export function docsStatus(d: Driver): DriverDocsStatus {
  return {
    blocking: d.docs.filter((x) => x.status === "missing" || x.status === "expired").length,
    underReview: d.docs.filter((x) => x.status === "pending").length,
    total: d.docs.length,
  };
}

export function docSummary(d: Driver | undefined): { text: string; className: string } {
  if (!d) return { text: "", className: "" };
  const t = getDict().company.docSummary;
  const { blocking, underReview, total } = docsStatus(d);
  if (blocking) return { text: t.blocking(blocking), className: "text-tone-red-fg" };
  if (underReview) return { text: t.review(underReview), className: "text-tone-amber-fg" };
  return { text: t.approved(total), className: "text-tone-green-fg" };
}

/* ---------- Actions: pure (state) => [next state, result] ---------- */

export type Action<A extends unknown[] = []> = (
  s: CompanyState,
  ...args: A
) => [CompanyState, ActionResult];

const ok = (id?: string): ActionResult => ({ ok: true, id });
const fail = (reason: "limit" | "conflict" | "invalid", message: string): ActionResult => ({
  ok: false,
  reason,
  message,
});

/** Can one more listing go live? */
export function publishCheck(s: CompanyState, listingId?: string): ActionResult {
  const t = getDict().company.errors;
  if (s.profile.verification !== "verified") {
    return fail("invalid", t.mustBeVerified);
  }
  const already = s.listings.some((l) => l.id === listingId && l.publish === "published");
  // Pricing from the API: only the free launch limit applies (the backend checks it too)
  if (s.pricing) {
    const limit = s.pricing.listingLimit;
    if (!already && limit && activeListingCount(s) >= limit) {
      return fail("invalid", t.limit(getDict().company.launch.name, limit));
    }
    return ok();
  }
  const plan = currentPlan(s);
  if (!plan || ["cancelled", "expired"].includes(s.subscription!.status)) {
    return fail("invalid", t.choosePlan);
  }
  if (!already && activeListingCount(s) >= plan.listingLimit) {
    return fail("limit", t.limit(planName(plan), plan.listingLimit));
  }
  return ok();
}

export const setPublish: Action<[string, PublishStatus]> = (s, listingId, status) => {
  if (status === "published") {
    const check = publishCheck(s, listingId);
    if (!check.ok) return [s, check];
  }
  return [
    { ...s, listings: s.listings.map((l) => (l.id === listingId ? { ...l, publish: status } : l)) },
    ok(listingId),
  ];
};

export type ListingDraft = Omit<Listing, "id" | "publish"> & { id?: string };

/** Creates or updates a listing and its new units; publishes if asked and allowed. */
export const saveListing: Action<[ListingDraft, { code: string; plate: string }[], boolean]> = (
  s,
  draft,
  newUnits,
  publish,
) => {
  const id = draft.id ?? `l${Date.now()}`;
  const existing = findListing(s, id);
  const listing: Listing = { ...draft, id, publish: existing?.publish ?? "draft" };
  const units: Unit[] = newUnits.map((u, i) => ({
    id: `${id}-n${Date.now()}${i}`,
    listingId: id,
    code: u.code.trim().toUpperCase(),
    plate: u.plate.trim().toUpperCase(),
    mileage: draft.mileage,
    status: "active",
    blocks: [],
  }));
  const saved: CompanyState = {
    ...s,
    listings: existing
      ? s.listings.map((l) => (l.id === id ? listing : l))
      : [...s.listings, listing],
    units: [...s.units, ...units],
  };
  if (!publish) return [saved, ok(id)];

  const [published, result] = setPublish(saved, id, "published");
  return result.ok ? [published, ok(id)] : [saved, { ...result, id }];
};

/** Replaces all listings and vehicles with the ones loaded from the API. */
export const setListings: Action<[Listing[], Unit[]]> = (s, listings, units) => [
  { ...s, listings, units, synced: true },
  ok(),
];

/** Adds or replaces one listing (and its vehicles) returned by the API. */
export const addListing: Action<[Listing, Unit[]]> = (s, listing, units) => [
  {
    ...s,
    listings: [...s.listings.filter((l) => l.id !== listing.id), listing],
    units: [...s.units.filter((u) => u.listingId !== listing.id), ...units],
  },
  ok(listing.id),
];

/** GET /company/bookings: replaces the demo bookings and drivers. */
export const setBookings: Action<[{ booking: Booking; driver: Driver | null }[]]> = (s, rows) => {
  const drivers = new Map<string, Driver>();
  rows.forEach(({ driver }) => driver && drivers.set(driver.id, driver));
  return [{ ...s, bookings: rows.map((r) => r.booking), drivers: [...drivers.values()], bookingsLoaded: true }, ok()];
};

/** One booking back from the API (after a decision, a message or opening it). */
export const upsertBooking: Action<[{ booking: Booking; driver: Driver | null }]> = (s, { booking, driver }) => [
  {
    ...s,
    bookings: s.bookings.some((b) => b.id === booking.id)
      ? s.bookings.map((b) => (b.id === booking.id ? booking : b))
      : [booking, ...s.bookings],
    drivers: driver ? [...s.drivers.filter((d) => d.id !== driver.id), driver] : s.drivers,
  },
  ok(booking.id),
];

/** GET /company/subscription result. */
export const loadPricing: Action<[CompanyPricing]> = (s, pricing) => [{ ...s, pricing }, ok()];

/** After POST /company/listings/:id/sponsor: the new end date ("YYYY-MM-DD"). */
export const setSponsoredUntil: Action<[string, string | undefined]> = (s, listingId, until) => [
  { ...s, listings: s.listings.map((l) => (l.id === listingId ? { ...l, urgentUntil: until } : l)) },
  ok(until),
];

export const removeListing: Action<[string]> = (s, listingId) => [
  {
    ...s,
    listings: s.listings.filter((l) => l.id !== listingId),
    units: s.units.filter((u) => u.listingId !== listingId),
  },
  ok(),
];

export const activateUrgent: Action<[string, number]> = (s, listingId, days) => {
  const l = findListing(s, listingId)!;
  const from = isUrgent(l) ? l.urgentUntil! : TODAY;
  const until = addDaysIso(from, days);
  const option = URGENT_OPTIONS.find((o) => o.days === days)!;
  return [
    {
      ...s,
      listings: s.listings.map((x) => (x.id === listingId ? { ...x, urgentUntil: until } : x)),
      invoices: [
        {
          date: TODAY,
          number: `U-${Date.now().toString().slice(-6)} · ${getDict().company.errors.urgentInvoice(days)}`,
          amount: option.price,
          status: "Paid",
        },
        ...s.invoices,
      ],
    },
    ok(until),
  ];
};

export const addUnit: Action<[string, string, string]> = (s, listingId, code, plate) => {
  const t = getDict().company.errors;
  const clean = code.trim().toUpperCase();
  if (!clean) return [s, fail("invalid", t.codeRequired)];
  if (s.units.some((u) => u.code === clean)) {
    return [s, fail("invalid", t.codeExists(clean))];
  }
  const unit: Unit = {
    id: `${listingId}-n${Date.now()}`,
    listingId,
    code: clean,
    plate: plate.trim().toUpperCase(),
    mileage: findListing(s, listingId)?.mileage ?? 0,
    status: "active",
    blocks: [],
  };
  return [{ ...s, units: [...s.units, unit] }, ok(unit.id)];
};

export const setUnitStatus: Action<[string, UnitStatus]> = (s, unitId, status) => [
  { ...s, units: s.units.map((u) => (u.id === unitId ? { ...u, status } : u)) },
  ok(unitId),
];

export const addBlock: Action<[string, string, string, string]> = (s, unitId, start, end, reason) => {
  const t = getDict().company.errors;
  if (!start || !end || end <= start) return [s, fail("invalid", t.invalidPeriod)];
  const clash = unitConflicts(s, unitId, start, end).find((c) => c.type === "booking");
  if (clash) {
    return [s, fail("conflict", t.overlap(clash.ref))];
  }
  return [
    {
      ...s,
      units: s.units.map((u) =>
        u.id === unitId ? { ...u, blocks: [...u.blocks, { start, end, reason: reason.trim() }] } : u,
      ),
    },
    ok(unitId),
  ];
};

/* ---------- Booking decisions ---------- */

function updateBooking(
  s: CompanyState,
  id: string,
  change: (b: Booking) => Partial<Booking>,
  status?: BookingStatus,
  message?: string,
): CompanyState {
  const at = nowStamp();
  return {
    ...s,
    bookings: s.bookings.map((b) => {
      if (b.id !== id) return b;
      const next = { ...b, ...change(b) };
      if (status) {
        next.status = status;
        next.history = [...b.history, { status, at, by: s.profile.name }];
      }
      if (message) next.messages = [...next.messages, { from: "company", text: message, at }];
      return next;
    }),
  };
}

export type Decision =
  | { kind: "approve"; unitId: string | null; message?: string }
  | { kind: "assign"; unitId: string }
  | { kind: "info" | "reject"; message: string }
  | { kind: "handover" | "complete" };

export const decide: Action<[string, Decision]> = (s, bookingId, d) => {
  const b = findBooking(s, bookingId)!;

  if ((d.kind === "approve" || d.kind === "assign") && d.unitId) {
    const unit = findUnit(s, d.unitId)!;
    if (!unitFree(s, unit, b.start, b.end, b.id)) {
      return [s, fail("conflict", getDict().company.errors.notFree(unit.code))];
    }
  }

  switch (d.kind) {
    case "approve":
      return [updateBooking(s, b.id, () => ({ unitId: d.unitId }), "confirmed", d.message || undefined), ok()];
    case "assign":
      return [updateBooking(s, b.id, () => ({ unitId: d.unitId })), ok()];
    case "info":
      return [updateBooking(s, b.id, () => ({}), "documents_required", d.message), ok()];
    case "reject":
      return [updateBooking(s, b.id, () => ({}), "declined", d.message), ok()];
    case "handover":
      if (!b.unitId) return [s, fail("invalid", getDict().company.errors.assignFirst)];
      return [updateBooking(s, b.id, () => ({}), "active"), ok()];
    case "complete":
      return [updateBooking(s, b.id, () => ({}), "completed"), ok()];
  }
};

export const sendMessage: Action<[string, string]> = (s, bookingId, text) => [
  updateBooking(s, bookingId, () => ({}), undefined, text.trim()),
  ok(),
];

/* ---------- Subscription and profile ---------- */

export const startSubscription: Action<[PlanId]> = (s, planId) => {
  const plan = PLANS.find((p) => p.id === planId)!;
  return [
    {
      ...s,
      subscription: plan.price
        ? { planId, status: "trial", trialEnd: addDaysIso(TODAY, 30), renewal: addDaysIso(TODAY, 30), card: null }
        : { planId, status: "active", renewal: addDaysIso(TODAY, 30), card: null },
    },
    ok(),
  ];
};

export const changePlan: Action<[PlanId]> = (s, planId) =>
  s.subscription
    ? [{ ...s, subscription: { ...s.subscription, planId } }, ok()]
    : startSubscription(s, planId);

export const cancelSubscription: Action = (s) => [
  { ...s, subscription: s.subscription && { ...s.subscription, status: "cancelled" } },
  ok(),
];

export const reactivateSubscription: Action = (s) => [
  {
    ...s,
    subscription: s.subscription && {
      ...s.subscription,
      status: "active",
      renewal: addDaysIso(TODAY, 30),
    },
  },
  ok(),
];

export const updatePaymentMethod: Action<[string]> = (s, card) => [
  {
    ...s,
    subscription: s.subscription && {
      ...s.subscription,
      card,
      status: s.subscription.status === "past_due" ? "active" : s.subscription.status,
    },
  },
  ok(),
];

export const updateProfile: Action<[Partial<CompanyState["profile"]>]> = (s, changes) => [
  { ...s, profile: { ...s.profile, ...changes } },
  ok(),
];

/** The profile as first loaded from the API (replaces the placeholder one). */
export const loadProfile: Action<[CompanyState["profile"]]> = (s, profile) => [
  { ...s, profile, profileLoaded: true },
  ok(),
];
